import { useCallback, useEffect, useState } from 'react';
import type {
  ForecastFilter,
  ForecastMetric,
  ForecastPage,
  ForecastPort,
  ForecastSummary,
  ForecastUtility,
  ForecastUtilityFilter,
  ForecastWatchPage,
} from '../../consumer/forecasting';
import { INITIAL_FORECAST_FILTER } from '../../consumer/forecasting';
import { useMonitoringRead, type MonitoringReadState } from '../../consumer/polling';
import type { RiskPageProps } from '../../consumer/workbench';
import { riskHref } from '../../consumer/workbench';
import { MonitoringReadNotice } from '../Summary';
import {
  evaluationLocationHref,
  parseEvaluationLocation,
  type EvaluationLocation,
} from './evaluation-location';
import './management.css';

export interface EvaluationSelection {
  metric: '' | ForecastMetric;
  horizon: string;
  algorithm: string;
  from: string;
  to: string;
}
function browserLocation() {
  return parseEvaluationLocation(typeof window === 'undefined' ? '' : window.location.search);
}
const number = (n: number | null) => (n === null ? '不可用（分母不足）' : String(n));
const instant = (time: string) => (time ? new Date(time).toISOString() : '');
export function utilityFilterFor(selection: EvaluationSelection): ForecastUtilityFilter {
  return {
    ...(selection.metric ? { metric: selection.metric } : {}),
    ...(selection.horizon ? { horizonSeconds: Number(selection.horizon) } : {}),
    ...(selection.algorithm ? { algorithmVersion: selection.algorithm } : {}),
    ...(selection.from ? { from: instant(selection.from) } : {}),
    ...(selection.to ? { to: instant(selection.to) } : {}),
  };
}
export function historyFilterFor(
  selection: EvaluationSelection,
  areaId: string,
  offset = 0,
): ForecastFilter {
  return {
    ...INITIAL_FORECAST_FILTER,
    areaId,
    metric: selection.metric,
    from: instant(selection.from),
    to: instant(selection.to),
    offset,
    limit: 10,
  };
}

export function HeldoutEvidence() {
  return (
    <section className="nm-heldout nm-management-card" data-nm-heldout-evidence>
      <div className="nm-management-heading">
        <div>
          <h2>已冻结的合成持有集结论</h2>
          <p>历史评估报告 · 2026-10-09 · VS03</p>
        </div>
        <strong className="nm-utility-warning">NEEDS_IMPROVEMENT · 预测效用需改进</strong>
      </div>
      <p>
        LOCAL_LINEAR_V1 相比 LAST_VALUE_V1：MAE 在 6 组中的 5 组更差，RMSE 在 6 组中的 6
        组更差。不得呈现为高可靠火险预报。
      </p>
      <details>
        <summary>查看固定报告的分母、冻结版本和来源</summary>
        <p>
          首次持有集：温度与颗粒物 × 5 / 10 / 20 秒；2149 个预测、6447 个唯一目标。5491
          个实际匹配目标、956 个不可评估目标。两种算法复用同一目标和输入；未重新调参。
        </p>
        <p>
          12894 个算法行：10982 已评估、1912
          不可评估。不可用不计入准确率；失效是不可用的子集。合成窗口重叠，结论不能外推到真实消防场景。
        </p>
        <p className="nm-provenance">
          模型冻结：208ed829。报告源：docs/phase2/execution/evidence/P2-NO-VS03/supervisor/Delivery.md；本轮输入
          master：1155fc7c93c1b452bfb40859a88d747eb7a1a027。
        </p>
      </details>
    </section>
  );
}

export function UtilityResults({ data }: { readonly data: ForecastUtility }) {
  return (
    <div data-nm-forecast-utility>
      <p className="nm-scope">
        当前实例全区域 · {data.generatedAt} · Predictive Utility: {data.predictiveUtility}
      </p>
      <p>
        下表是当前数据库的动态统计，与上方冻结持有集分别展示。MAE / RMSE 保留各指标单位；Precision /
        Recall 的分母分别为 TP+FP / TP+FN，不是平台准确率。
      </p>
      {!data.groups.length && (
        <p className="nm-empty">此筛选暂无效用分组；不能推断预测通过或区域安全。</p>
      )}
      <div className="nm-table-wrap">
        <table className="nm-table">
          <caption>实际后端预测效用 · 同组算法比较</caption>
          <thead>
            <tr>
              <th>指标 / 时间窗 / 算法</th>
              <th>生成 / 及时发布</th>
              <th>可评估 / 不可用 / 失效</th>
              <th>MAE / RMSE</th>
              <th>TP / FP / FN / TN</th>
              <th>Precision / Recall</th>
            </tr>
          </thead>
          <tbody>
            {data.groups.map((g) => (
              <tr
                key={`${g.metric}-${g.horizonSeconds}-${g.algorithmVersion}-${g.profileVersion}`}
                data-nm-utility-group
              >
                <td>
                  {g.metric} / {g.unit} / +{g.horizonSeconds}s
                  <small>
                    {g.algorithmVersion} · {g.profileVersion}
                  </small>
                </td>
                <td>
                  {g.generated} / {g.timely}
                </td>
                <td>
                  {g.evaluable} / {g.unavailable} / {g.invalidated}
                </td>
                <td>
                  {number(g.mae)} / {number(g.rmse)}
                  <small>{g.unit}</small>
                </td>
                <td>
                  {g.tp} / {g.fp} / {g.fn} / {g.tn}
                </td>
                <td>
                  {number(g.precision)} / {number(g.recall)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="p01-muted">
        仅可评估行参与误差与分类统计；不可用及失效仍显示在总分母中，失效不额外叠加到不可用。不同指标单位不合并。
      </p>
      <details>
        <summary>本次动态统计限制</summary>
        {data.limitations.map((l, i) => (
          <p key={i}>{l}</p>
        ))}
      </details>
    </div>
  );
}

export function ForecastHistoryResults({
  data,
  areaId,
}: {
  readonly data: ForecastPage;
  readonly areaId: string;
}) {
  return (
    <>
      <p className="nm-scope">
        后端生成 {data.generatedAt} · 共 {data.total} 个已保存预测
      </p>
      {!data.items.length && (
        <p className="nm-empty">当前范围没有已保存预测；无记录不表示区域安全。</p>
      )}
      <div className="nm-table-wrap">
        <table className="nm-table">
          <caption>不可变历史预测 · 每页最多 10 条</caption>
          <thead>
            <tr>
              <th>区域 / 来源 / 指标</th>
              <th>预测生成依据时刻</th>
              <th>发布 / 模型</th>
              <th>回验与依据</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((f) => (
              <tr key={f.forecastId} data-nm-forecast-id={f.forecastId}>
                <td>
                  {f.areaId} / {f.sourceId}
                  <small>
                    {f.metric} / {f.unit} · epoch {f.epoch}
                  </small>
                </td>
                <td>
                  {f.origin}
                  <small>发布 {f.issuedAt ?? '未知（无及时发布证据）'}</small>
                </td>
                <td>
                  {f.publishStatus}
                  <small>
                    {f.algorithmVersion} / {f.benchmarkVersion}
                  </small>
                </td>
                <td>
                  <a href={riskHref(`/forecasts/${encodeURIComponent(f.forecastId)}`, areaId)}>
                    查看预测与回验 →
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
function ForecastHistory({
  port,
  areaId,
  selection,
  offset,
  setOffset,
}: {
  readonly port: ForecastPort;
  readonly areaId: string;
  readonly selection: EvaluationSelection;
  readonly offset: number;
  readonly setOffset: (offset: number) => void;
}) {
  const filter = historyFilterFor(selection, areaId, offset);
  const key = JSON.stringify(filter);
  const read = useCallback(
    (signal: AbortSignal) => port.forecasts(historyFilterFor(selection, areaId, offset), signal),
    [port, selection, areaId, offset],
  );
  const result = useMonitoringRead(`evaluation/forecasts/${key}`, read);
  const data = result.state.data;
  return (
    <section data-nm-evaluation-history>
      <MonitoringReadNotice state={result.state} retry={result.retry} label="历史预测" />
      {data && (
        <>
          <ForecastHistoryResults data={data} areaId={areaId} />
          <HistoryPagination
            offset={offset}
            total={data.total}
            ready={result.state.status === 'READY'}
            setOffset={setOffset}
          />
        </>
      )}
    </section>
  );
}
function HistoryPagination({
  offset,
  total,
  ready,
  setOffset,
}: {
  readonly offset: number;
  readonly total: number;
  readonly ready: boolean;
  readonly setOffset: (offset: number) => void;
}) {
  return (
    <div className="nm-pagination">
      <button
        type="button"
        disabled={!ready || offset === 0}
        onClick={() => setOffset(Math.max(0, offset - 10))}
      >
        上一页记录
      </button>
      <span>
        第 {Math.floor(offset / 10) + 1} 页 · 共 {total} 条
      </span>
      <button
        type="button"
        disabled={!ready || offset + 10 >= total || offset + 10 > 1000000}
        onClick={() => setOffset(offset + 10)}
      >
        下一页记录
      </button>
    </div>
  );
}
export function WatchHistoryResults({
  data,
  areaId,
}: {
  readonly data: ForecastWatchPage;
  readonly areaId: string;
}) {
  return (
    <>
      <p className="nm-scope">
        {data.generatedAt} · 共 {data.total} 个关注修订；保留已到期、未命中及失效记录。
      </p>
      {!data.items.length && <p className="nm-empty">当前范围暂无预测关注修订；不能推断安全。</p>}
      <div className="nm-table-wrap">
        <table className="nm-table">
          <caption>预测关注修订历史</caption>
          <thead>
            <tr>
              <th>区域 / 关注</th>
              <th>修订 / 状态</th>
              <th>目标 / 原因</th>
              <th>原始预测</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(({ watch: w, revision }) => (
              <tr key={`${w.watchId}-${revision}`} data-nm-watch-id={w.watchId}>
                <td>
                  {w.areaId}
                  <small>{w.watchId}</small>
                </td>
                <td>
                  第 {revision} 版<small>{w.status}</small>
                </td>
                <td>
                  +{w.horizonSeconds}s · {w.targetAt}
                  <small>{w.reason}</small>
                </td>
                <td>
                  {w.forecastIds.map((id) => (
                    <a
                      key={id}
                      className="nm-record-link"
                      href={riskHref(`/forecasts/${encodeURIComponent(id)}`, areaId, {
                        horizon: String(w.horizonSeconds),
                      })}
                    >
                      查看原始预测 →
                    </a>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
function WatchHistory({
  port,
  areaId,
  selection,
  offset,
  setOffset,
}: {
  readonly port: ForecastPort;
  readonly areaId: string;
  readonly selection: EvaluationSelection;
  readonly offset: number;
  readonly setOffset: (offset: number) => void;
}) {
  const read = useCallback(
    (signal: AbortSignal) =>
      port.watches(
        { areaId, from: instant(selection.from), to: instant(selection.to), offset, limit: 10 },
        signal,
      ),
    [port, areaId, selection, offset],
  );
  const result = useMonitoringRead(
    `evaluation/watches/${areaId}/${selection.from}/${selection.to}/${offset}`,
    read,
  );
  const data = result.state.data;
  return (
    <section>
      <p>关注按区域与时间范围查询；指标、算法与时间窗筛选用于效用统计，关注可能关联多个指标。</p>
      <MonitoringReadNotice state={result.state} retry={result.retry} label="关注历史" />
      {data && (
        <>
          <WatchHistoryResults data={data} areaId={areaId} />
          <HistoryPagination
            offset={offset}
            total={data.total}
            ready={result.state.status === 'READY'}
            setOffset={setOffset}
          />
        </>
      )}
    </section>
  );
}
export function SourceQualifications({
  data,
  areaId,
  metric,
}: {
  readonly data: ForecastSummary;
  readonly areaId: string;
  readonly metric: string;
}) {
  const sources = data.sources.filter(
    (s) => (!areaId || s.areaId === areaId) && (!metric || s.metric === metric),
  );
  return (
    <>
      <p className="nm-scope">
        来源资格生成：{data.generatedAt} · {areaId || '全部区域'}
      </p>
      {!sources.length && <p className="nm-empty">范围内无可展示来源；预测覆盖未知。</p>}
      <div className="nm-table-wrap">
        <table className="nm-table">
          <caption>不能预测及可预测来源 · 保留失败原因</caption>
          <thead>
            <tr>
              <th>区域 / 来源</th>
              <th>资格 / 原因</th>
              <th>样本 / 时间跨度</th>
              <th>已保存结果</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.sourceId}>
                <td>
                  {s.areaId} / {s.sourceId}
                  <small>
                    {s.metric} / {s.unit}
                  </small>
                </td>
                <td>
                  {s.status}
                  <small>{s.reason}</small>
                </td>
                <td>
                  {s.sampleCount} 条 / {s.spanSeconds} 秒
                  <small>{s.latestObservedAt ?? '无观测'}</small>
                </td>
                <td>
                  {s.latestForecastId ? (
                    <a
                      href={riskHref(
                        `/forecasts/${encodeURIComponent(s.latestForecastId)}`,
                        areaId,
                      )}
                    >
                      查看已保存预测 →
                    </a>
                  ) : (
                    '尚无已保存预测'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
export function CurrentUtilityRead({
  state,
  retry,
}: {
  readonly state: MonitoringReadState<ForecastUtility>;
  readonly retry: () => void;
}) {
  return (
    <>
      <MonitoringReadNotice
        state={state.status === 'ERROR' ? { ...state, data: null } : state}
        retry={retry}
        label="预测效用"
      />
      {state.status === 'READY' && state.data && <UtilityResults data={state.data} />}
      {state.status === 'ERROR' && (
        <p className="nm-empty">
          当前动态效用不可用；最后有效表格已隐藏，冻结历史结论仍可单独查看。
        </p>
      )}
    </>
  );
}
export function CurrentQualificationsRead({
  state,
  retry,
  areaId,
  metric,
}: {
  readonly state: MonitoringReadState<ForecastSummary>;
  readonly retry: () => void;
  readonly areaId: string;
  readonly metric: string;
}) {
  return (
    <>
      <MonitoringReadNotice
        state={state.status === 'ERROR' ? { ...state, data: null } : state}
        retry={retry}
        label="来源预测资格"
      />
      {state.status === 'READY' && state.data && (
        <SourceQualifications data={state.data} areaId={areaId} metric={metric} />
      )}
      {state.status === 'ERROR' && (
        <p className="nm-empty">当前来源资格不可用；不能使用上次资格推断当前覆盖。</p>
      )}
    </>
  );
}
function Qualifications({
  port,
  areaId,
  metric,
}: {
  readonly port: ForecastPort;
  readonly areaId: string;
  readonly metric: string;
}) {
  const read = useCallback((signal: AbortSignal) => port.summary(signal), [port]);
  const result = useMonitoringRead(`evaluation/qualifications/${areaId}/${metric}`, read);
  return (
    <section>
      <p>这里展示当前来源资格，不按历史时间范围筛选；历史错误与未命中结果请查看已保存预测回验。</p>
      <CurrentQualificationsRead
        state={result.state}
        retry={result.retry}
        areaId={areaId}
        metric={metric}
      />
    </section>
  );
}
function EvaluationContent({
  port,
  areaId,
}: {
  readonly port: ForecastPort;
  readonly areaId: string;
}) {
  const [location, setLocation] = useState(browserLocation);
  const [draft, setDraft] = useState(() => location.selection);
  const [error, setError] = useState('');
  const { selection, tab } = location;
  const navigate = (next: EvaluationLocation) => {
    if (typeof window !== 'undefined')
      window.history.pushState(null, '', evaluationLocationHref(areaId, next));
    setLocation(next);
  };
  useEffect(() => {
    const restore = () => {
      const next = browserLocation();
      setLocation(next);
      setDraft(next.selection);
      setError('');
    };
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, []);
  const read = useCallback(
    (signal: AbortSignal) => port.utility(utilityFilterFor(selection), signal),
    [port, selection],
  );
  const result = useMonitoringRead(`evaluation/utility/${JSON.stringify(selection)}`, read);
  return (
    <>
      <section className="nm-management-card" data-nm-evaluation-live>
        <h2>当前实例模型效果</h2>
        <p>
          效用 API 提供全部区域的统计；导航范围 {areaId || '全部区域'}{' '}
          仅应用于下方预测历史与来源资格。
        </p>
        <form
          className="nm-filters"
          onSubmit={(event) => {
            event.preventDefault();
            if (draft.from && draft.to && Date.parse(draft.from) > Date.parse(draft.to)) {
              setError('开始时间必须早于或等于结束时间。');
              return;
            }
            setError('');
            navigate({ ...location, selection: { ...draft }, forecastOffset: 0, watchOffset: 0 });
          }}
        >
          <label>
            预测指标
            <select
              aria-label="模型效果指标"
              value={draft.metric}
              onChange={(e) =>
                setDraft({ ...draft, metric: e.target.value as EvaluationSelection['metric'] })
              }
            >
              <option value="">全部指标</option>
              <option value="TEMPERATURE">温度 / degC</option>
              <option value="PARTICULATE">颗粒物 / ug/m3</option>
            </select>
          </label>
          <label>
            预测时间窗
            <select
              aria-label="模型效果时间窗"
              value={draft.horizon}
              onChange={(e) => setDraft({ ...draft, horizon: e.target.value })}
            >
              <option value="">全部时间窗</option>
              {[5, 10, 20].map((h) => (
                <option key={h} value={h}>
                  +{h} 秒
                </option>
              ))}
            </select>
          </label>
          <label>
            比较算法
            <select
              aria-label="模型效果算法"
              value={draft.algorithm}
              onChange={(e) => setDraft({ ...draft, algorithm: e.target.value })}
            >
              <option value="">两种算法</option>
              <option value="LOCAL_LINEAR_V1">LOCAL_LINEAR_V1</option>
              <option value="LAST_VALUE_V1">LAST_VALUE_V1 基准</option>
            </select>
          </label>
          <label>
            生成开始时间
            <input
              aria-label="模型效果开始时间"
              type="datetime-local"
              step="0.001"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            />
          </label>
          <label>
            生成结束时间
            <input
              aria-label="模型效果结束时间"
              type="datetime-local"
              step="0.001"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            />
          </label>
          <button type="submit">应用模型效果筛选</button>
        </form>
        {error && (
          <p className="nm-error" role="status">
            {error}
          </p>
        )}
        <p className="nm-scope">
          已应用：{selection.metric || '全部指标'} /{' '}
          {selection.horizon ? `+${selection.horizon} 秒` : '全部时间窗'} /{' '}
          {selection.algorithm || '两种算法'} / {selection.from || '起始不限'} →{' '}
          {selection.to || '结束不限'}
        </p>
        <CurrentUtilityRead state={result.state} retry={result.retry} />
      </section>
      <section className="nm-management-card" data-nm-evaluation-records>
        <h2>核对历史与失败依据</h2>
        <p>
          历史范围：{areaId || '全部区域'}
          。预测列表按指标、区域和生成时间分页；算法及时间窗只筛选上方效用。
        </p>
        <div className="nm-management-tabs" role="group" aria-label="模型效果依据视图">
          {(
            [
              ['forecasts', '历史预测与回验'],
              ['watches', '预测关注历史'],
              ['sources', '来源资格与失败原因'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              aria-pressed={tab === id}
              onClick={() => navigate({ ...location, tab: id })}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === 'forecasts' && (
          <ForecastHistory
            key={`${areaId}/${JSON.stringify(selection)}`}
            port={port}
            areaId={areaId}
            selection={selection}
            offset={location.forecastOffset}
            setOffset={(offset) => navigate({ ...location, forecastOffset: offset })}
          />
        )}
        {tab === 'watches' && (
          <WatchHistory
            key={`${areaId}/${JSON.stringify(selection)}`}
            port={port}
            areaId={areaId}
            selection={selection}
            offset={location.watchOffset}
            setOffset={(offset) => navigate({ ...location, watchOffset: offset })}
          />
        )}
        {tab === 'sources' && (
          <Qualifications port={port} areaId={areaId} metric={selection.metric} />
        )}
      </section>
    </>
  );
}
export function EvaluationPage({ forecastPort, areaId }: RiskPageProps) {
  return (
    <section className="nm-management nm-evaluation-page" data-nm-evaluation-page>
      <div className="nm-management-intro">
        <h2>模型效果与回验</h2>
        <p>比较实验模型与最近值基准，保留未命中、不可评估及失效样本。</p>
        <a href={riskHref('', areaId)}>返回风险与预警总览 →</a>
      </div>
      <section className="nm-management-card" data-historical-entry>
        <h2>真实历史气象站 · 逐小时离线实验</h2>
        <p>
          查看独立保存的 NOAA GHCNh 回测、四方法基准比较与不可评估样本。+1 / +3 / +6
          小时结果与下方合成秒级预测分别评价。
        </p>
        <a href={riskHref('/evaluation/historical', areaId)}>进入历史离线实验 →</a>
      </section>
      <HeldoutEvidence />
      {forecastPort ? (
        <EvaluationContent key={areaId} port={forecastPort} areaId={areaId} />
      ) : (
        <p className="nm-error" role="status">
          预测效用接口不可用；当前结果未知。
        </p>
      )}
    </section>
  );
}
